import { useEffect, useState, type ReactNode } from 'react';
import {
  BrowserRouter,
  Link,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './auth';
import { EmptyState, Spinner } from './components/ui';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordPages';
import { MembersPage } from './pages/MembersPage';
import { MemberPage } from './pages/MemberPage';
import { RatesPage } from './pages/RatesPage';
import { RatesYearPage } from './pages/RatesYearPage';
import { JoinPage } from './pages/JoinPage';
import { MyAccountPage } from './pages/MyAccountPage';
import { SubscriptionsPage } from './pages/SubscriptionsPage';
import { StatementImportPage } from './pages/StatementImportPage';
import { EmailPage } from './pages/EmailPage';
import { EmailTemplatesPage } from './pages/EmailTemplatesPage';
import { EmailLogPage } from './pages/EmailLogPage';
import { EmailRemindersPage } from './pages/EmailRemindersPage';
import { MentoringPage } from './pages/MentoringPage';
import { CoursesPage } from './pages/CoursesPage';
import { ApiariesPage } from './pages/ApiariesPage';
import { ReturnsPage } from './pages/ReturnsPage';
import { AgmPage } from './pages/AgmPage';
import { AddMemberPage } from './pages/AddMemberPage';
import { MemberImportPage } from './pages/MemberImportPage';
import { DashboardPage } from './pages/DashboardPage';
import { NewsletterImagesPage } from './pages/NewsletterImagesPage';
import { NewsletterPage } from './pages/NewsletterPage';
import { NewsletterSendPage } from './pages/NewsletterSendPage';
import { NewslettersPage } from './pages/NewslettersPage';

/**
 * Route guards here are presentation, not security.
 *
 * The API checks permissions on every endpoint and will refuse regardless of
 * what this component decides. All this does is keep people out of screens
 * that would only show them an error — which matters for volunteers, but is
 * not what is protecting the data.
 */
function Protected({ permission, children }: { permission?: string; children: ReactNode }) {
  const { status, can } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <Spinner label="Checking your account" />;

  if (status === 'signed-out') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (permission !== undefined && !can(permission)) {
    return (
      <EmptyState title="You do not have access to this">
        If you think you should, ask whoever looks after permissions on the committee.
      </EmptyState>
    );
  }

  return <>{children}</>;
}

/**
 * The header, and on a phone the menu behind it.
 *
 * Seven nav items do not fit a phone, so below 820px they collapse behind a
 * button. The button says "Menu" as well as showing the three lines: a
 * hamburger is only obvious to people who already know what it is, and a fair
 * number of this association's members do not.
 *
 * It closes when you go somewhere, when you press Escape, and it can be
 * reached and operated from the keyboard, because some of these members use
 * one rather than a mouse.
 */
function Shell({ children }: { children: ReactNode }) {
  const { identity, signOut, can } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Tapping a link and finding the menu still covering the page is the
  // commonest way these get this wrong.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div className="shell">
      <header className="topbar">
        <Link to="/" className="brand">
          Demo Beekeepers <span className="muted">Association</span>
        </Link>

        <button
          type="button"
          className="menu-toggle"
          aria-expanded={menuOpen}
          aria-controls="main-nav"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="menu-bars" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          {menuOpen ? 'Close' : 'Menu'}
        </button>

        {/* Nav and the account links collapse together, or the account links
            would still overflow a phone on their own. */}
        <div className="topbar-collapse" id="main-nav" data-open={menuOpen ? 'yes' : 'no'}>
        <nav className="nav">
          {can('committee_area') ? (
            <NavLink to="/dashboard" className={({ isActive }) => (isActive ? 'on' : '')}>
              Dashboard
            </NavLink>
          ) : null}
          {can('members_view') ? (
            <NavLink to="/members" className={({ isActive }) => (isActive ? 'on' : '')}>
              Members
            </NavLink>
          ) : null}
          {can('subscriptions_view') ? (
            <NavLink to="/subscriptions" className={({ isActive }) => (isActive ? 'on' : '')}>
              Subscriptions
            </NavLink>
          ) : null}
          {can('email_send') ? (
            <NavLink to="/email" className={({ isActive }) => (isActive ? 'on' : '')}>
              Email
            </NavLink>
          ) : null}
          {can('mentoring_manage') ? (
            <NavLink to="/mentoring" className={({ isActive }) => (isActive ? 'on' : '')}>
              Mentoring
            </NavLink>
          ) : null}
          {can('members_view') ? (
            <NavLink to="/apiaries" className={({ isActive }) => (isActive ? 'on' : '')}>
              Apiaries
            </NavLink>
          ) : null}
          {can('rates_manage') ? (
            <NavLink to="/rates" className={({ isActive }) => (isActive ? 'on' : '')}>
              Rates &amp; settings
            </NavLink>
          ) : null}
        </nav>

        <div className="who">
          {identity ? (
            <>
              <NavLink to="/my-account" className={({ isActive }) => `muted small${isActive ? ' on' : ''}`}>
                {identity.member.first_name} {identity.member.last_name}
              </NavLink>
              <button type="button" className="link" onClick={() => void signOut()}>
                Sign out
              </button>
            </>
          ) : null}
        </div>
        </div>
      </header>

      <main className="content">{children}</main>
    </div>
  );
}

/** Sends each signed-in person to the page that is actually theirs. */
function Landing() {
  const { status, can } = useAuth();

  if (status === 'loading') return <Spinner label="Checking your account" />;
  if (status === 'signed-out') return <Navigate to="/login" replace />;

  return <Navigate to={can('committee_area') ? '/dashboard' : '/my-account'} replace />;
}

/**
 * The route tree, without a router around it. Split out so the demo build can
 * mount the same screens inside a MemoryRouter — see src/demo.tsx. Production
 * always goes through App below.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/join" element={<JoinPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route
        path="/my-account"
        element={
          <Protected>
            <Shell>
              <MyAccountPage />
            </Shell>
          </Protected>
        }
      />

      <Route
        path="/members"
        element={
          <Protected permission="members_view">
            <Shell>
              <MembersPage />
            </Shell>
          </Protected>
        }
      />
      {/* Before /members/:id, or "new" is read as an id. */}
      <Route
        path="/members/new"
        element={
          <Protected permission="members_edit">
            <Shell>
              <AddMemberPage />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/members/import"
        element={
          <Protected permission="members_edit">
            <Shell>
              <MemberImportPage />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/members/:id"
        element={
          <Protected permission="members_view">
            <Shell>
              <MemberPage />
            </Shell>
          </Protected>
        }
      />

      <Route
        path="/subscriptions"
        element={
          <Protected permission="subscriptions_view">
            <Shell>
              <SubscriptionsPage />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/subscriptions/import"
        element={
          <Protected permission="payments_record">
            <Shell>
              <StatementImportPage />
            </Shell>
          </Protected>
        }
      />

      <Route
        path="/email"
        element={
          <Protected permission="email_send">
            <Shell>
              <EmailPage />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/email/templates"
        element={
          <Protected permission="email_send">
            <Shell>
              <EmailTemplatesPage />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/email/newsletters"
        element={
          <Protected permission="email_send">
            <Shell>
              <NewslettersPage />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/email/newsletters/:id"
        element={
          <Protected permission="email_send">
            <Shell>
              <NewsletterPage />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/email/newsletters/:id/send"
        element={
          <Protected permission="email_send">
            <Shell>
              <NewsletterSendPage />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/email/newsletter-images"
        element={
          <Protected permission="email_send">
            <Shell>
              <NewsletterImagesPage />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/email/log"
        element={
          <Protected permission="email_send">
            <Shell>
              <EmailLogPage />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/email/reminders"
        element={
          <Protected permission="email_schedule">
            <Shell>
              <EmailRemindersPage />
            </Shell>
          </Protected>
        }
      />

      <Route
        path="/mentoring"
        element={
          <Protected permission="mentoring_manage">
            <Shell>
              <MentoringPage />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/mentoring/courses"
        element={
          <Protected permission="mentoring_manage">
            <Shell>
              <CoursesPage />
            </Shell>
          </Protected>
        }
      />

      <Route
        path="/reports/agm"
        element={
          <Protected permission="subscriptions_view">
            <Shell>
              <AgmPage />
            </Shell>
          </Protected>
        }
      />

      <Route
        path="/returns"
        element={
          <Protected permission="subscriptions_view">
            <Shell>
              <ReturnsPage />
            </Shell>
          </Protected>
        }
      />

      <Route
        path="/apiaries"
        element={
          <Protected permission="members_view">
            <Shell>
              <ApiariesPage />
            </Shell>
          </Protected>
        }
      />

      <Route
        path="/rates"
        element={
          <Protected permission="rates_manage">
            <Shell>
              <RatesPage />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/rates/:id"
        element={
          <Protected permission="rates_manage">
            <Shell>
              <RatesYearPage />
            </Shell>
          </Protected>
        }
      />

      <Route
        path="/dashboard"
        element={
          <Protected permission="committee_area">
            <Shell>
              <DashboardPage />
            </Shell>
          </Protected>
        }
      />

      {/*
        Where you land depends on who you are. A committee member gets the
        dashboard; an ordinary member with no committee_area gets their own
        account, because a list of committee chores would mean nothing to
        them — and they cannot reach it anyway.
      */}
      <Route path="/" element={<Landing />} />
      <Route
        path="*"
        element={
          <Shell>
            <EmptyState title="Page not found">
              <Link to="/members">Back to members</Link>
            </EmptyState>
          </Shell>
        }
      />
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
